import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import { format, addDays, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PatientChatWidget from '@/components/PatientChatWidget';
import { formatMoney } from '@/lib/currency';
import { timezoneLabel, todayInTimezone } from '@/lib/timezones';
import BookingContactFields from '@/components/BookingContactFields';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CARD_THEMES, themeToCSS } from '@/components/cardThemes';
import { getCoverDataURL } from '@/components/coverGenerator';
import { getAICoverUrl } from '@/components/aiCovers';
import { MessageCircle, Phone, Clock, ArrowRight, Users, Check, ChevronDown, ChevronLeft, ChevronRight, Star, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = API.replace('/api', '');
import { PrivacyConsent } from '@/components/PrivacyConsent';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ProfilePage({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Booking
  const [bookStep, setBookStep] = useState(-1); // -1=hidden, 0=format, 1=date+time, 2=info, 3=done
  const [fmt, setFmt] = useState(null);
  const [dateIdx, setDateIdx] = useState(0);
  const [selTime, setSelTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [searchedAllDates, setSearchedAllDates] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', contactMethod: '', contactValue: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [profileConsent, setProfileConsent] = useState(false);
  const [reminderLink, setReminderLink] = useState('');
  // Waitlist
  const [waitlist, setWaitlist] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  // Lightbox
  const [lightbox, setLightbox] = useState(null);
  // FAQ
  const [openFaq, setOpenFaq] = useState(null);
  const slotsRequestRef = useRef(0);

  useEffect(() => {
    api.get(`/public/profile/${slug}`).then(({ data }) => {
      setP(data);
      // SEO meta tags
      document.title = `${data.name} — ${data.specialization || 'Специалист'}`;
      const desc = data.bio || data.specialization || '';
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
      metaDesc.setAttribute('content', desc.slice(0, 160));
      // OG tags
      [['og:title', data.name], ['og:description', desc.slice(0, 160)], ['og:type', 'profile'], ['og:url', window.location.href]].forEach(([prop, val]) => {
        let el = document.querySelector(`meta[property="${prop}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
        el.setAttribute('content', val);
      });
      if (data.avatar) {
        let ogImg = document.querySelector('meta[property="og:image"]');
        if (!ogImg) { ogImg = document.createElement('meta'); ogImg.setAttribute('property', 'og:image'); document.head.appendChild(ogImg); }
        ogImg.setAttribute('content', `${BASE}/api/avatars/${data.avatar}`);
      }
    }).catch(() => setError(true)).finally(() => setLoading(false));
    return () => { document.title = 'ProPraxis'; };
  }, [slug]);

  const dates = Array.from(
    { length: 14 },
    (_, i) => addDays(parseISO(todayInTimezone(p?.timezone)), i),
  );
  const currentDate = dates[dateIdx];

  const loadDateSlots = async (index, selectedFormat = fmt) => {
    if (!selectedFormat || !p) return;
    const requestId = ++slotsRequestRef.current;
    setDateIdx(index);
    setSelTime('');
    setSlotsLoading(true);
    setSlotsError('');
    setSearchedAllDates(false);
    try {
      const { data } = await api.get(`/public/available-slots`, {
        params: { date: format(dates[index], 'yyyy-MM-dd'), format_id: selectedFormat.id, psychologist_id: p.psychologist_id },
      });
      if (requestId === slotsRequestRef.current) setSlots(data);
    } catch (err) {
      if (requestId === slotsRequestRef.current) {
        setSlots([]);
        setSlotsError(err.response?.data?.detail || 'Не удалось загрузить свободное время. Попробуйте ещё раз.');
      }
    } finally {
      if (requestId === slotsRequestRef.current) setSlotsLoading(false);
    }
  };

  const selectFormat = async (selectedFormat) => {
    const requestId = ++slotsRequestRef.current;
    setFmt(selectedFormat);
    setBookStep(1);
    setDateIdx(0);
    setSelTime('');
    setSlots([]);
    setSlotsError('');
    setSearchedAllDates(false);
    setSlotsLoading(true);

    for (let index = 0; index < dates.length; index += 1) {
      try {
        const { data } = await api.get(`/public/available-slots`, {
          params: { date: format(dates[index], 'yyyy-MM-dd'), format_id: selectedFormat.id, psychologist_id: p.psychologist_id },
        });
        if (requestId !== slotsRequestRef.current) return;
        if (data.length > 0) {
          setDateIdx(index);
          setSlots(data);
          setSlotsLoading(false);
          return;
        }
      } catch (err) {
        if (requestId !== slotsRequestRef.current) return;
        setSlotsError(err.response?.data?.detail || 'Не удалось загрузить свободное время. Попробуйте ещё раз.');
        setSlotsLoading(false);
        return;
      }
    }

    if (requestId === slotsRequestRef.current) {
      setSearchedAllDates(true);
      setSlotsLoading(false);
    }
  };

  const submitBooking = async () => {
    const patientName = form.name.trim();
    const patientEmail = form.email.trim().toLowerCase();
    if (!patientName || !patientEmail || !selTime || !fmt) {
      toast.error('Заполните имя и email');
      return;
    }
    if (!EMAIL_PATTERN.test(patientEmail)) {
      toast.error('Проверьте формат email');
      return;
    }
    if (!form.contactMethod) {
      toast.error('Выберите удобный способ связи');
      return;
    }
    if (form.contactMethod !== 'email' && !form.contactValue.trim()) {
      toast.error(form.contactMethod === 'telegram' ? 'Укажите Telegram' : 'Укажите телефон');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/book`, {
        patient_name: patientName,
        patient_phone: form.contactMethod === 'phone' ? form.contactValue.trim() : '',
        patient_email: patientEmail,
        contact_method: form.contactMethod,
        contact_value: form.contactMethod === 'email' ? patientEmail : form.contactValue.trim(),
        date: format(currentDate, 'yyyy-MM-dd'), time: selTime, format_id: fmt.id, link_id: '', notes: form.notes.trim(),
        psychologist_id: p.psychologist_id,
      });
      setBookStep(3);
      if (data?.id) {
        try {
          const { data: rl } = await api.get(`/public/reminder-link/${data.id}`);
          setReminderLink(rl.link || '');
        } catch {}
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setSubmitting(false);
  };

  const submitWaitlist = async () => {
    if (!form.name || !form.phone) return;
    setSubmitting(true);
    try { await api.post(`/public/waitlist`, { name: form.name, phone: form.phone, psychologist_id: p.psychologist_id }); setWaitlistDone(true); } catch { toast.error('Ошибка'); }
    setSubmitting(false);
  };

  const fallbackTheme = CARD_THEMES.forest;
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: fallbackTheme.bg }}><p style={{ color: fallbackTheme.textMuted }}>Загрузка...</p></div>;
  if (error || !p) return <div className="min-h-screen flex items-center justify-center" style={{ background: fallbackTheme.bg }}><p style={{ color: fallbackTheme.textMuted }}>Профиль не найден</p></div>;

  const avatar = p.avatar ? `${BASE}/api/avatars/${p.avatar}` : '';
  const theme = CARD_THEMES[p.card_theme] || CARD_THEMES.forest;
  const tv = themeToCSS(theme);
  const coverUrl = p.cover_type === 'ai' && p.cover_pattern?.startsWith('ai:')
    ? getAICoverUrl(p.cover_pattern.replace('ai:', ''))
    : p.cover_type === 'generated' && p.cover_pattern?.startsWith('svg:')
    ? getCoverDataURL(p.cover_pattern.split(':')[1], 1200, 400, theme.accent, theme.bg, parseInt(p.cover_pattern.split(':')[2]) || 42)
    : p.cover ? `${BASE}/api/covers/${p.cover}` : '';
  const bookableFormats = Array.isArray(p.bookable_formats) ? p.bookable_formats : [];
  const bookableFormatsById = new Map(bookableFormats.map((item) => [item.id, item]));
  const hasInlineBookableFormats = (p.formats || []).some((item) => bookableFormatsById.has(item.id));

  return (
    <div className="min-h-screen" style={{ background: theme.bg, color: theme.text }}>
      {/* Demo banner */}
      {p.is_demo && (
        <div data-testid="demo-banner" className="fixed top-0 left-0 right-0 z-50 py-1.5 text-center text-xs font-medium tracking-wide"
          style={{ background: theme.accent, color: '#fff' }}>
          Демо-визитка — все данные вымышлены
        </div>
      )}
      {/* Clinic doctor banner */}
      {p.is_clinic_doctor && p.clinic_slug && (
        <div className="fixed top-0 left-0 right-0 z-50 py-2 text-center text-xs font-medium"
          style={{ background: theme.card, borderBottom: `1px solid ${theme.border}`, marginTop: p.is_demo ? '28px' : 0 }}>
          <a href={`/organization/${p.clinic_slug}`} style={{ color: theme.accent }}>
            ← {p.clinic_name || 'Вернуться на сайт клиники'}
          </a>
        </div>
      )}
      {/* Cover as absolute background */}
      {coverUrl && (
        <div className="absolute top-0 left-0 right-0 z-0">
          <img src={coverUrl} alt="" className="w-full" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 0%, ${theme.bg} 100%)` }} />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 pt-8 pb-12 relative z-10">
        {!coverUrl && <div className="h-12" />}
        {coverUrl && <div className="h-24 sm:h-32" />}
        {/* Avatar + Name card */}
        <div className="text-center mb-8">
          <div className="w-36 h-36 sm:w-40 sm:h-40 rounded-full mx-auto mb-5 overflow-hidden border-4 shadow-xl" style={{ borderColor: theme.bg, background: theme.card }}>
            {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full flex items-center justify-center"><span className="text-5xl font-heading" style={{ color: theme.accent }}>{(p.name || '?')[0]}</span></div>}
          </div>
          <div className="inline-block rounded-2xl px-8 py-4" style={coverUrl ? { background: `${theme.bg}e6`, backdropFilter: 'blur(12px)' } : {}}>
            <h1 className="font-heading text-2xl sm:text-3xl" style={{ color: theme.text }}>{p.name}</h1>
            {p.specialization && <p className="mt-1.5 text-sm font-medium" style={{ color: theme.textSec }}>{p.specialization}</p>}
            {!p.accepting_clients && <Badge className="mt-2 text-xs" style={{ background: 'var(--app-accent-muted)', color: theme.accent }}>Запись закрыта</Badge>}
          </div>
        </div>

        {/* Tags */}
        {p.tags?.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {p.tags.map((t, i) => <Badge key={i} className="text-xs px-3 py-1" style={{ background: theme.card, color: theme.accent, border: `1px solid ${theme.border}` }}>{t}</Badge>)}
          </div>
        )}

        {/* Bio */}
        {p.bio && bookStep < 0 && !waitlist && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              <h2 className="font-heading text-base mb-3" style={{ color: theme.text }}>О специалисте</h2>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: theme.textSec }}>{p.bio}</p>
            </CardContent>
          </Card>
        )}

        {/* Certificates */}
        {p.certificates?.length > 0 && bookStep < 0 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              <h2 className="font-heading text-base mb-3" style={{ color: theme.text }}>Дипломы и сертификаты</h2>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {p.certificates.map((c, i) => (
                  <div key={i} className="shrink-0 w-28 h-28 rounded-lg overflow-hidden border cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderColor: theme.border }} onClick={() => setLightbox(i)}>
                    <img src={`${BASE}/api/certificates/${c}`} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Formats */}
        {bookStep < 0 && !waitlist && p.formats?.length > 0 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              <h2 className="font-heading text-base" style={{ color: theme.text }}>Форматы работы</h2>
              {p.accepting_clients && hasInlineBookableFormats && (
                <p className="text-xs mt-1 mb-3" style={{ color: theme.textMuted }}>
                  Выберите подходящий формат, чтобы посмотреть свободное время.
                </p>
              )}
              {p.booking_note && (
                <div className="mb-3 rounded-lg border p-3 text-xs leading-relaxed whitespace-pre-line"
                  style={{ background: theme.cardDeep, borderColor: theme.border, color: theme.textSec }}>
                  <span className="font-medium" style={{ color: theme.text }}>Перед записью</span>
                  <p className="mt-1">{p.booking_note}</p>
                </div>
              )}
              {(!p.accepting_clients || !hasInlineBookableFormats) && <div className="mb-3" />}
              <div className="space-y-2">
                {p.formats.map((f, i) => {
                  const bookableFormat = bookableFormatsById.get(f.id);
                  const details = (
                    <div className="min-w-0">
                      <p className="text-sm font-medium" style={{ color: theme.text }}>{f.name}</p>
                      {f.description && <p className="text-xs mt-0.5" style={{ color: theme.textSec }}>{f.description}</p>}
                      <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: theme.textMuted }}><Clock size={11} /> {f.duration} мин.</p>
                    </div>
                  );

                  if (!p.accepting_clients || !bookableFormat) {
                    return (
                      <div key={f.id || i} className="flex items-center justify-between gap-4 p-3 rounded-lg" style={{ background: theme.cardDeep }}>
                        {details}
                        {p.show_prices && f.price > 0 && <span className="shrink-0 text-sm font-semibold" style={{ color: theme.accent }}>{formatMoney(f.price, f.currency || p.currency)}</span>}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={f.id || i}
                      type="button"
                      onClick={() => selectFormat(bookableFormat)}
                      className="w-full flex items-center justify-between gap-4 p-3 rounded-lg border text-left transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                      style={{ background: theme.cardDeep, borderColor: theme.border, outlineColor: theme.accent }}
                      aria-label={`Выбрать формат «${f.name}»`}
                    >
                      {details}
                      <span className="shrink-0 text-right">
                        {p.show_prices && f.price > 0 && <span className="block text-sm font-semibold" style={{ color: theme.accent }}>{formatMoney(f.price, f.currency || p.currency)}</span>}
                        <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium" style={{ color: theme.accent }}>
                          Выбрать <ArrowRight size={13} />
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inline Booking */}
        {bookStep >= 0 && bookStep < 3 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              {bookStep === 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => setBookStep(-1)} className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}><ChevronLeft size={13} /> Назад</button>
                    <h3 className="font-heading text-base" style={{ color: theme.text }}>Выберите формат</h3>
                    <div />
                  </div>
                  <p className="text-[11px] mb-3" style={{ color: theme.textMuted }}>
                    Время специалиста: {timezoneLabel(p.timezone)}
                  </p>
                  <div className="space-y-2">{bookableFormats.map(f => (
                    <button key={f.id} onClick={() => selectFormat(f)}
                      className="w-full text-left p-3 rounded-lg border transition-colors" style={{ background: theme.cardDeep, borderColor: theme.border, color: theme.text }}>
                      <span className="text-sm font-medium">{f.name}</span>
                      <span className="text-xs ml-2" style={{ color: theme.textMuted }}>{f.duration} мин.{p.show_prices && f.price ? ` / ${formatMoney(f.price, f.currency || p.currency)}` : ''}</span>
                      {f.description && <p className="text-xs mt-1" style={{ color: theme.textMuted }}>{f.description}</p>}
                    </button>
                  ))}</div>
                </div>
              )}
              {bookStep === 1 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => { slotsRequestRef.current += 1; setBookStep(hasInlineBookableFormats ? -1 : 0); setSelTime(''); setSlotsError(''); }} className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}><ChevronLeft size={13} /> Назад</button>
                    <span className="text-xs" style={{ color: theme.textMuted }}>{fmt?.name}</span>
                  </div>
                  <p className="text-[11px] mb-3" style={{ color: theme.textMuted }}>
                    Время специалиста: {timezoneLabel(p.timezone)}
                  </p>
                  {/* Date swiper */}
                  <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1">
                    {dates.map((d, i) => {
                      const isActive = i === dateIdx;
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <button key={i} onClick={() => loadDateSlots(i)}
                          className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-lg text-xs transition-all ${isActive ? 'font-semibold' : ''}`}
                          style={{ background: isActive ? 'var(--app-accent)' : 'var(--app-card-deep)', color: isActive ? 'white' : isWeekend ? 'var(--app-text-faint)' : 'var(--app-text-secondary)',
                            border: `1px solid ${isActive ? 'var(--app-accent)' : 'var(--app-border)'}` }}>
                          <span className="text-[10px] uppercase">{format(d, 'EEE', { locale: ru })}</span>
                          <span className="text-base font-semibold">{format(d, 'd')}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Time slots */}
                  {slotsLoading ? <p className="text-xs text-center py-4" style={{ color: theme.textMuted }}>Ищем ближайшее свободное время...</p> :
                    slotsError ? (
                      <div className="text-center py-4 space-y-3">
                        <p className="text-xs" style={{ color: theme.textSec }}>{slotsError}</p>
                        <Button size="sm" onClick={() => loadDateSlots(dateIdx)} style={{ background: theme.cardDeep, color: theme.textSec, border: `1px solid ${theme.border}` }}>
                          Повторить
                        </Button>
                      </div>
                    ) : slots.length === 0 ? <p className="text-xs text-center py-4" style={{ color: theme.textFaint }}>{searchedAllDates ? 'В ближайшие 14 дней свободного времени нет' : 'На эту дату свободного времени нет'}</p> : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map(t => (
                        <Button key={t} size="sm" onClick={() => { setSelTime(t); setBookStep(2); }}
                          style={selTime === t ? { background: theme.accent, color: 'white' } : { background: theme.cardDeep, color: theme.textSec, border: '1px solid var(--app-border)' }}>
                          {t}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {bookStep === 2 && (
                <div className="space-y-3">
                  <button onClick={() => setBookStep(1)} className="text-xs flex items-center gap-1" style={{ color: theme.textMuted }}><ChevronLeft size={13} /> Назад</button>
                  <p className="text-xs" style={{ color: theme.accent }}>{fmt?.name} / {format(currentDate, 'd MMMM', { locale: ru })} в {selTime}</p>
                  <BookingContactFields
                    value={form}
                    onChange={setForm}
                    palette={{
                      text: theme.text,
                      secondary: theme.textSec,
                      muted: theme.textMuted,
                      faint: theme.textFaint,
                      input: theme.cardDeep,
                      surface: theme.cardDeep,
                      border: theme.border,
                      accent: theme.accent,
                      accentMuted: theme.accentMuted,
                    }}
                  />
                  <PrivacyConsent checked={profileConsent} onChange={setProfileConsent} />
                  <Button onClick={submitBooking} disabled={submitting || !form.name.trim() || !form.email.trim() || !form.contactMethod || (form.contactMethod !== 'email' && !form.contactValue.trim()) || !profileConsent} className="w-full text-white" style={{ background: theme.accent }}>
                    {submitting ? 'Запись...' : 'Записаться'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {bookStep === 3 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                <Check size={28} style={{ color: theme.accent }} /></div>
              <h3 className="font-heading text-xl mb-2" style={{ color: theme.text }}>Записано!</h3>
              <p className="text-sm" style={{ color: theme.textSec }}>Ожидайте подтверждения от специалиста.</p>
              {reminderLink && (
                <a href={reminderLink} target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
                  style={{ background: theme.accentMuted, color: theme.accent, border: `1px solid ${theme.accent}` }}>
                  Получить напоминание в Telegram
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Waitlist */}
        {waitlist && !waitlistDone && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-heading text-base" style={{ color: theme.text }}>Лист ожидания</h3>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Имя" style={{ background: theme.cardDeep, borderColor: theme.border, color: theme.text }} />
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Телефон" style={{ background: theme.cardDeep, borderColor: theme.border, color: theme.text }} />
              <PrivacyConsent checked={profileConsent} onChange={setProfileConsent} />
              <Button onClick={submitWaitlist} disabled={submitting || !form.name || !form.phone || !profileConsent} className="w-full text-white" style={{ background: theme.accent }}>
                {submitting ? '...' : 'Встать в очередь'}
              </Button>
            </CardContent>
          </Card>
        )}
        {waitlistDone && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-8 text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}><Users size={28} style={{ color: theme.accent }} /></div>
              <h3 className="font-heading text-xl mb-2" style={{ color: theme.text }}>Вы в очереди!</h3>
              <p className="text-sm" style={{ color: theme.textSec }}>Специалист свяжется с вами.</p>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        {bookStep < 0 && !waitlist && !waitlistDone && (
          p.accepting_clients && !hasInlineBookableFormats ? (
            <Button className="w-full h-12 text-base text-white mb-5" style={{ background: theme.accent }} onClick={() => setBookStep(0)}>
              Записаться <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : !p.accepting_clients ? (
            <Button className="w-full h-12 text-base text-white mb-5" style={{ background: theme.accent }} onClick={() => setWaitlist(true)}>
              <Users size={18} className="mr-2" /> Встать в очередь
            </Button>
          ) : null
        )}

        {/* FAQ */}
        {p.faq?.length > 0 && bookStep < 0 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              <h2 className="font-heading text-base mb-3" style={{ color: theme.text }}>Частые вопросы</h2>
              <div className="space-y-1">
                {p.faq.map((item, i) => (
                  <div key={i} className="border-b last:border-0" style={{ borderColor: theme.border }}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between py-3 text-left text-sm" style={{ color: theme.text }}>
                      {item.q}
                      <ChevronDown size={14} className={`shrink-0 ml-2 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: theme.textMuted }} />
                    </button>
                    {openFaq === i && <p className="pb-3 text-xs leading-relaxed" style={{ color: theme.textSec }}>{item.a}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        {p.reviews?.length > 0 && bookStep <= 0 && (
          <Card className="mb-5" style={{ background: theme.card, borderColor: theme.border }}>
            <CardContent className="p-5">
              <h2 className="font-heading text-base mb-3 flex items-center gap-1.5" style={{ color: theme.text }}><Star size={16} /> Отзывы</h2>
              <div className="space-y-3">
                {p.reviews.map((r, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ background: theme.cardDeep }}>
                    {r.photo && (
                      <img src={r.photo.startsWith('http') ? r.photo : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/reviews/${r.photo}`}
                        alt="" className="w-full h-36 object-cover rounded-md mb-2" />
                    )}
                    <p className="text-sm leading-relaxed mb-2" style={{ color: theme.textSec }}>"{r.text}"</p>
                    <p className="text-xs font-medium" style={{ color: theme.textMuted }}>— {r.author || 'Аноним'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts */}
        {p.show_contacts && p.contacts && (p.contacts.telegram || p.contacts.whatsapp) && (
          <div className="flex justify-center gap-3 mb-6">
            {p.contacts.telegram && <a href={`https://t.me/${p.contacts.telegram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--app-card)', color: theme.textSec, border: '1px solid var(--app-border)' }}><MessageCircle size={15} /> Telegram</a>}
            {p.contacts.whatsapp && <a href={`https://wa.me/${p.contacts.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--app-card)', color: theme.textSec, border: '1px solid var(--app-border)' }}><Phone size={15} /> WhatsApp</a>}
          </div>
        )}

        {/* Watermark */}
        {p.watermark && (
          <div className="fixed bottom-0 left-0 right-0 py-3 text-center z-50" style={{ background: theme.card, borderTop: '1px solid ' + theme.border }}>
            <a href="/" className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: theme.textMuted }}>
              <img src="/logo.png" alt="" className="w-5 h-5 rounded" />
              Сделано в <span style={{ color: theme.accent }}>ProPraxis</span>
            </a>
          </div>
        )}
      </div>

      {/* Certificate Lightbox */}
      {lightbox !== null && p.certificates && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)} style={{ background: 'rgba(0,0,0,0.9)' }}>
          <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setLightbox(null)}><X size={24} /></button>
          {p.certificates.length > 1 && <>
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + p.certificates.length) % p.certificates.length); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center"><ChevronLeft size={20} /></button>
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % p.certificates.length); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center"><ChevronRight size={20} /></button>
          </>}
          <img src={`${BASE}/api/certificates/${p.certificates[lightbox]}`} alt="" className="max-w-full max-h-[85vh] rounded-lg" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Chat Widget */}
      {p.psychologist_id && (
        <PatientChatWidget
          ownerId={p.is_clinic_doctor ? '' : p.psychologist_id}
          bookingLinkId=""
          patientName=""
          patientPhone=""
          {...(p.is_clinic_doctor ? { ownerId: p.psychologist_id } : { ownerId: p.psychologist_id })}
        />
      )}
    </div>
  );
}
